/**
 * Car silhouettes, one per body style.
 *
 * Drawn here rather than pulled from an icon set because lucide has a single generic car glyph, and
 * a marketplace that cannot show the difference between a karavan and a džip at a glance is missing
 * the one visual cue buyers actually sort by.
 *
 * Every path shares a 64×28 canvas with the axle line at y=20, so the shapes line up when they sit
 * next to each other in a row of filters.
 */

/** Mirrors `KojaKola.Domain.Enums.BodyType`. */
export const BodyStyle = {
  Hatchback: 1,
  Sedan: 2,
  Estate: 3,
  Suv: 4,
  Coupe: 5,
  Convertible: 6,
  Mpv: 7,
  Van: 8,
  PickUp: 9,
  OffRoad: 10,
} as const;

const SHAPES: Record<number, string> = {
  [BodyStyle.Hatchback]:
    'M3 20 V17.2 C3 15.9 3.8 15 5.2 14.7 L11.8 13.3 L17.6 8.2 C18.9 7.1 20.4 6.5 22.1 6.5 H36.4 C38.4 6.5 40 7.2 41.2 8.6 L45.8 13.9 L54.4 14.9 C57.4 15.3 59 16.4 59 18.2 V20 Z',
  [BodyStyle.Sedan]:
    'M3 20 V17.4 C3 16 3.9 15.1 5.4 14.8 L11.6 13.4 L17.2 8.6 C18.5 7.5 20 7 21.7 7 H34.6 C36.4 7 37.8 7.5 39 8.5 L44.4 13.1 L54.6 14.6 C57.6 15.1 59.4 16.3 59.4 18.2 V20 Z',
  [BodyStyle.Estate]:
    'M3 20 V17.2 C3 15.9 3.8 15 5.2 14.7 L11.8 13.3 L17.4 8 C18.7 6.9 20.2 6.3 21.9 6.3 H50.6 C52.4 6.3 53.8 6.7 54.9 7.6 C56.6 8.9 57.6 10.7 57.6 13 V20 Z',
  [BodyStyle.Suv]:
    'M3 20 V15.6 C3 14.2 3.9 13.3 5.4 13 L11.4 11.6 L16.6 6.2 C17.9 4.9 19.4 4.3 21.2 4.3 H47.6 C49.6 4.3 51.2 4.9 52.4 6.2 L56.6 10.8 C58 12.3 58.8 13.8 58.8 15.6 V20 Z',
  [BodyStyle.Coupe]:
    'M3 20 V17.6 C3 16.3 3.8 15.4 5.2 15.1 L12 13.6 L19.6 9 C21.4 7.9 23.4 7.3 25.6 7.3 H33 C35.4 7.3 37.4 7.9 39 9.2 L46.4 14.6 L55 15.4 C57.8 15.7 59.4 16.8 59.4 18.4 V20 Z',
  [BodyStyle.Convertible]:
    'M3 20 V17.4 C3 16 3.9 15.1 5.4 14.8 L19.8 12.8 H44.2 L55.2 14.8 C57.9 15.3 59.4 16.4 59.4 18.2 V20 Z M21.6 12.6 L27.4 6.4 L30.2 8.2 L25.2 12.6 Z',
  [BodyStyle.Mpv]:
    'M3 20 V14.4 C3 12.4 3.9 10.9 5.7 9.9 L13.4 5.6 C14.7 4.9 16 4.6 17.5 4.6 H46.8 C49.6 4.6 51.8 5.3 53.5 6.7 C55.8 8.6 57 11 57 13.9 V20 Z',
  // A blunt one-box nose, so it cannot be confused with the off-roader below.
  [BodyStyle.Van]:
    'M3 20 V9.4 C3 6.6 4.6 4.4 7.6 3.2 C9 2.7 10.4 2.4 12 2.4 H48.4 C51.4 2.4 53.8 3.2 55.6 4.8 C57.7 6.7 58.8 9 58.8 11.8 V20 Z',
  // The rear wall of the bed is the only thing that reads as "pickup" at 16px.
  [BodyStyle.PickUp]:
    'M3 20 V16.2 C3 14.8 3.9 13.9 5.4 13.6 L11.4 12.2 L16.6 7 C17.9 5.9 19.4 5.3 21.2 5.3 H32.8 C34.6 5.3 36 5.9 37 7 L40.8 11.4 H56.4 V8.6 H59.4 V20 Z',
  // Flat bonnet, upright screen, square roof: the profile of a proper off-roader.
  [BodyStyle.OffRoad]:
    'M3 20 V13.2 C3 11.9 3.8 11.1 5.4 10.9 L15.2 10.9 L18.8 5 C19.5 4 20.6 3.5 22.1 3.5 H52 C54 3.5 55.4 4.2 56.4 5.6 C58.1 7.9 59 10.4 59 13.2 V20 Z',
};

const GENERIC = SHAPES[BodyStyle.Hatchback];

interface Props {
  /** A `BodyType` value. Anything unrecognised falls back to a generic profile. */
  bodyType?: number | null;
  className?: string;
  /** Larger wheels read better on the taller shapes. */
  title?: string;
}

export function CarSilhouette({ bodyType, className, title }: Props) {
  const shape = (bodyType != null && SHAPES[bodyType]) || GENERIC;
  const bigWheels = bodyType === BodyStyle.Suv || bodyType === BodyStyle.OffRoad;
  const radius = bigWheels ? 4.4 : 3.9;

  return (
    <svg
      viewBox="0 0 64 28"
      fill="currentColor"
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      className={className}
    >
      {title && <title>{title}</title>}
      <path d={shape} />
      <circle cx="17.5" cy="20" r={radius} />
      <circle cx="46.5" cy="20" r={radius} />
    </svg>
  );
}
